import { defineStore } from 'pinia';

import AirMasterService from '@/services/air-imports/master.service';

import { useState } from '@/composables/common/useState';
import { useToggle } from '@/composables/useToggle';
import { useManifestHasInvoicedCharges } from '@/composables/charges/manifest/useManifestHasInvoicedCharges';
import { useToast } from '@/composables/useToast';
import i18n from '@/i18n';
import { useVirtualAirMblStore } from '@/store/modules/air-imports/useAirMblVirtualStore';
import { useQueryClient } from '@tanstack/vue-query';
import { AirMblEntity } from '@/types/air/mbl/air-mbl.entity';
import { MilestoneEntity } from '@/types/common/milestone.entity';
import { ComputedRef, computed } from 'vue';
import { AxiosError } from 'axios';
export const useAirMblStore = defineStore('AirMBL', () => {
  const toast = useToast();
  const virtualMblStore = useVirtualAirMblStore();

  const [mbl, setMbl] = useState<AirMblEntity | null>(null);
  const [milestones, setMilestones] = useState<MilestoneEntity[]>([]);

  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [readonly, toggleReadonly] = useToggle(true);
  const queryClient = useQueryClient();
  const { hasInvoicedCharges, checkManifestHasInvoicedCharges } = useManifestHasInvoicedCharges();

  const mblData: ComputedRef<AirMblEntity | null> = computed(() => mbl.value);

  const create = async (payload: any) => {
    // await validateContainer(payload.mblNumber, payload.containerNumber);
    const { data: result } = await AirMasterService.create(payload);
    if (!result) {
      return { success: false, message: result.message || i18n.t('ServerErrorTryAgainLater') };
    }
    return { success: true, message: i18n.t('MBLCreatedSuccessfully'), data: result };
  };

  const update = async ({ id, payload }: any) => {
    if (!id) {
      throw new Error('MBL data is incorrect.');
    }
    // await validateContainer(payload.mblNumber, payload.containerNumber, id);
    const { data: result } = await AirMasterService.update(payload, id);
    if (!result) {
      return { success: false, message: result.message || i18n.t('ServerErrorTryAgainLater') };
    }

    await fetchMbl(id);
    await fetchMilestones(id);
    toggleReadonly(true);

    return { success: true, message: i18n.t('MBLSavedSuccessfully') };
  };

  const deleteManifest = async (id: string) => {
    try {
      await AirMasterService.delete(id);
      await virtualMblStore.search();
    } catch (error) {
      toast.error((error as AxiosError).response?.data as string);
      throw error;
    }
  };

  const validateContainer = async (mblNumber: string, containerNumber: string, mblId?: string) => {
    const { data } = await AirMasterService.validateContainerStatus(
      mblNumber,
      containerNumber,
      mblId
    );
    if (!data.status) {
      toast.error('Duplication of MBL # / Container # ! Please change the values!');
      throw new Error('MBL#/Container# duplication');
    }
    return data.status;
  };

  const fetchMbl = async (id: any) => {
    try {
      const { data } = await AirMasterService.get(id);
      setMbl(data);
    } catch (e) {
      console.log('fetchMbl', e);
      throw e;
    }
  };

  const fetchMilestones = async (id: any) => {
    try {
      const { data } = await AirMasterService.getMilestones(id);
      setMilestones(data);
    } catch (e) {
      console.log('fetchMawbMilestones', e);
    }
  };

  const finalize = async (mblId: string) => {
    const { data } = await AirMasterService.validateForFinalize(mblId);
    if (!data.status) {
      let message = 'MBL is not ready for finalizing! <br />';
      if (!data.mblNumber) message = message.concat('- MBL # should be entered <br />');
      if (!data.containerNumber) message = message.concat('- Container # should be entered <br />');
      if (!data.customer) message = message.concat('- Customer should be selected <br />');
      if (!data.stripDate) message = message.concat('- Strip Date should be entered <br />');
      if (!data.closedate) message = message.concat('- File should not be closed <br />');
      if (!data.hbls) message = message.concat('- At least one valid HBL should be present <br />');
      return toast.info(message);
    }
    try {
      const { data: finalizationResponse } = await AirMasterService.finalize(mblId);
      return finalizationResponse;
    } catch (e) {
      toast.info(((e as AxiosError)?.response?.data as string) || 'Error while finalizing!');
    }
  };

  const updateCloseStatus = async (mblId: string) => {
    try {
      await AirMasterService.close(mblId);
      await virtualMblStore.search();
      // setMbl(mbl);
    } catch {
      toast.error('Error while closing hbl');
    }
  };

  const updateViewStatus = async (id: string, mbl: any) => {
    const payload = {
      ...mbl,
      isViewed: true,
    };
    await update({
      id,
      payload,
    });
    await queryClient.invalidateQueries(['receive-count']);
  };

  return {
    hasChanges,
    hasInvoicedCharges,
    setHasChanges,

    mblData,
    mbl,
    milestones,
    fetchMbl,
    updateViewStatus,
    fetchMilestones,

    readonly,
    toggleReadonly,
    checkManifestHasInvoicedCharges,

    finalize,
    create,
    update,
    deleteManifest,
    validateContainer,

    updateCloseStatus,
  };
});
